/**
 * Brain screen — Issue #39
 *
 * Q&A interface backed by GraphRAG (#22).
 * Calls POST /brain { query } via useAINative().api.call.
 * Degrades gracefully when the endpoint is unavailable (404/network error).
 *
 * Layout:
 *   – Prominent question input with keyboard submit (Enter / Cmd+Enter)
 *   – Four suggested example questions from the PRD
 *   – Answer area: response text + cited source memories (score / entity_id / content)
 *   – History rail: previously asked questions, click to re-run
 */

import React, { useCallback, useRef, useState } from "react";
import { useAINative } from "../providers/AINativeProvider.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GraphRAGCitation {
  index: number;
  id: string;
  entity_id: string | null;
  score: number;
}

interface GraphRAGResult {
  id: string;
  content: string;
  score: number;
  vector_score?: number;
  graph_boost?: number;
  entity_id: string | null;
  created_at?: string;
  memory_type?: string;
}

interface BrainResponse {
  answer?: string;
  context?: string;
  results?: GraphRAGResult[];
  citations?: GraphRAGCitation[];
}

type QueryStatus = "idle" | "loading" | "success" | "error" | "unavailable";

interface HistoryEntry {
  id: string;
  query: string;
  answer: string;
  citations: GraphRAGCitation[];
  results: GraphRAGResult[];
  timestamp: Date;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SUGGESTED_QUESTIONS = [
  "What am I forgetting?",
  "What should Eli call today?",
  "What delays construction?",
  "Which projects slipped?",
] as const;

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CitationCardProps {
  result: GraphRAGResult;
  index: number;
}

function CitationCard({ result, index }: CitationCardProps) {
  const scorePercent = Math.round(result.score * 100);
  const hasGraphBoost = typeof result.graph_boost === "number" && result.graph_boost > 0;
  const entityLabel = result.entity_id
    ? result.entity_id.replace(/^[^:]+:/, "")
    : null;

  return (
    <div className="brain-citation" role="listitem" aria-label={`Source ${index + 1}`}>
      <div className="brain-citation__header">
        <span className="brain-citation__index">[{index + 1}]</span>
        <div className="brain-citation__meta">
          {entityLabel && (
            <span className="brain-citation__entity" title={result.entity_id ?? undefined}>
              {entityLabel}
            </span>
          )}
          {hasGraphBoost && (
            <span className="brain-citation__graph-badge" title="Graph-connected node">
              graph
            </span>
          )}
        </div>
        <div className="brain-citation__score-wrap" title={`Relevance score: ${scorePercent}%`}>
          <div
            className="brain-citation__score-bar"
            style={{ width: `${scorePercent}%` }}
            role="progressbar"
            aria-valuenow={scorePercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Relevance ${scorePercent}%`}
          />
          <span className="brain-citation__score-label">{scorePercent}%</span>
        </div>
      </div>
      <p className="brain-citation__content">{result.content}</p>
    </div>
  );
}

interface HistoryItemProps {
  entry: HistoryEntry;
  onSelect: (query: string) => void;
}

function HistoryItem({ entry, onSelect }: HistoryItemProps) {
  const timeLabel = entry.timestamp.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <button
      className="brain-history__item"
      onClick={() => onSelect(entry.query)}
      aria-label={`Re-run: ${entry.query}`}
      title={`Asked at ${timeLabel} — click to re-run`}
    >
      <span className="brain-history__item-query">{entry.query}</span>
      <span className="brain-history__item-time">{timeLabel}</span>
    </button>
  );
}

// ─── Idle / empty state ───────────────────────────────────────────────────────

interface EmptyStateProps {
  onSelect: (q: string) => void;
}

function EmptyState({ onSelect }: EmptyStateProps) {
  return (
    <div className="brain-empty" aria-label="Brain empty state">
      <div className="brain-empty__glyph" aria-hidden="true">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3" />
          <path d="M16 24c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="24" cy="24" r="2.5" fill="currentColor" />
        </svg>
      </div>
      <p className="brain-empty__prompt">Ask anything about your projects, deals, and decisions.</p>
      <p className="brain-empty__hint">Try one of these:</p>
      <ul className="brain-suggestions" role="list" aria-label="Suggested questions">
        {SUGGESTED_QUESTIONS.map((q) => (
          <li key={q} role="listitem">
            <button
              className="brain-suggestions__chip"
              onClick={() => onSelect(q)}
              aria-label={`Ask: ${q}`}
            >
              <span className="brain-suggestions__chip-icon" aria-hidden="true">&rarr;</span>
              {q}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Unavailable / backend-missing state ─────────────────────────────────────

interface UnavailableStateProps {
  onSelect: (q: string) => void;
}

function UnavailableState({ onSelect }: UnavailableStateProps) {
  return (
    <div className="brain-unavailable" role="status" aria-label="Brain backend unavailable">
      <div className="brain-unavailable__header">
        <span className="brain-unavailable__dot" aria-hidden="true" />
        <span className="brain-unavailable__label">AI memory service unavailable</span>
      </div>
      <p className="brain-unavailable__desc">
        The AI memory service is starting up — try again in a moment.
      </p>
      <p className="brain-unavailable__hint">You can still explore the interface:</p>
      <ul className="brain-suggestions" role="list" aria-label="Suggested questions">
        {SUGGESTED_QUESTIONS.map((q) => (
          <li key={q} role="listitem">
            <button
              className="brain-suggestions__chip"
              onClick={() => onSelect(q)}
              aria-label={`Ask: ${q}`}
            >
              <span className="brain-suggestions__chip-icon" aria-hidden="true">&rarr;</span>
              {q}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Answer panel ─────────────────────────────────────────────────────────────

interface AnswerPanelProps {
  query: string;
  answer: string;
  results: GraphRAGResult[];
  citations: GraphRAGCitation[];
}

function AnswerPanel({ query, answer, results, citations }: AnswerPanelProps) {
  // Annotate [N] footnote markers in the answer text
  const annotatedAnswer = answer.replace(
    /\[(\d+)\]/g,
    (_match, n) => `[${n}]`,
  );

  return (
    <div className="brain-answer" role="region" aria-label="Brain answer">
      <div className="brain-answer__question-echo">
        <span className="brain-answer__question-label">Q</span>
        <span className="brain-answer__question-text">{query}</span>
      </div>

      <div className="brain-answer__body">
        <div className="brain-answer__text" aria-live="polite">
          {annotatedAnswer || (
            <span className="brain-answer__no-text">
              No synthesized answer — see sources below.
            </span>
          )}
        </div>
      </div>

      {results.length > 0 && (
        <div className="brain-answer__sources">
          <h3 className="brain-answer__sources-title">
            Sources
            <span className="brain-answer__sources-count">{results.length}</span>
          </h3>
          <div className="brain-citations" role="list" aria-label="Source citations">
            {results.slice(0, 8).map((result, i) => (
              <CitationCard key={result.id} result={result} index={i} />
            ))}
          </div>
        </div>
      )}

      {citations.length > 0 && results.length === 0 && (
        <div className="brain-answer__sources">
          <h3 className="brain-answer__sources-title">
            Citations
            <span className="brain-answer__sources-count">{citations.length}</span>
          </h3>
          <ul className="brain-inline-citations" role="list">
            {citations.map((c) => (
              <li key={c.id} className="brain-inline-citation" role="listitem">
                <span className="brain-inline-citation__index">[{c.index}]</span>
                {c.entity_id && (
                  <span className="brain-inline-citation__entity">{c.entity_id}</span>
                )}
                <span className="brain-inline-citation__score">
                  {Math.round(c.score * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Loading state ────────────────────────────────────────────────────────────

function ThinkingState({ query }: { query: string }) {
  return (
    <div className="brain-thinking" role="status" aria-label="Brain is thinking" aria-live="polite">
      <div className="brain-thinking__dots" aria-hidden="true">
        <span className="brain-thinking__dot" />
        <span className="brain-thinking__dot" />
        <span className="brain-thinking__dot" />
      </div>
      <span className="brain-thinking__label">
        Searching memory for <em>&ldquo;{query}&rdquo;</em>
      </span>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <div className="brain-error" role="alert" aria-label="Brain query error">
      <div className="brain-error__header">
        <span className="brain-error__icon" aria-hidden="true">!</span>
        <span className="brain-error__title">Query failed</span>
      </div>
      <p className="brain-error__message">{message}</p>
      <button className="brain-error__retry" onClick={onRetry} aria-label="Retry query">
        Retry
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function BrainPage() {
  const { api } = useAINative();

  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [answer, setAnswer] = useState("");
  const [results, setResults] = useState<GraphRAGResult[]>([]);
  const [citations, setCitations] = useState<GraphRAGCitation[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const currentQueryRef = useRef<string>("");

  const submitQuery = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) return;

      setActiveQuery(trimmed);
      setStatus("loading");
      setAnswer("");
      setResults([]);
      setCitations([]);
      setErrorMessage("");
      currentQueryRef.current = trimmed;

      try {
        const response = await api.call<BrainResponse>("/brain", {
          method: "POST",
          body: { query: trimmed },
        });

        // Bail if a newer query was submitted while this one was in-flight
        if (currentQueryRef.current !== trimmed) return;

        const newResults = response.results ?? [];
        const newCitations = response.citations ?? [];
        const newAnswer = response.answer ?? response.context ?? "";

        setAnswer(newAnswer);
        setResults(newResults);
        setCitations(newCitations);
        setStatus("success");

        // Prepend to history (keep latest 20)
        const entry: HistoryEntry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          query: trimmed,
          answer: newAnswer,
          citations: newCitations,
          results: newResults,
          timestamp: new Date(),
        };
        setHistory((prev) => [entry, ...prev].slice(0, 20));
      } catch (err: unknown) {
        if (currentQueryRef.current !== trimmed) return;

        const msg = err instanceof Error ? err.message : "Unknown error";
        const is404 =
          msg.includes("404") ||
          msg.toLowerCase().includes("not found") ||
          msg.toLowerCase().includes("unavailable");

        if (is404) {
          setStatus("unavailable");
        } else {
          setStatus("error");
          setErrorMessage(msg);
        }
      }
    },
    [api],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void submitQuery(query);
    },
    [query, submitQuery],
  );

  const handleSuggestionSelect = useCallback(
    (q: string) => {
      setQuery(q);
      void submitQuery(q);
      inputRef.current?.focus();
    },
    [submitQuery],
  );

  const handleHistorySelect = useCallback(
    (q: string) => {
      setQuery(q);
      void submitQuery(q);
      inputRef.current?.focus();
    },
    [submitQuery],
  );

  const handleRetry = useCallback(() => {
    void submitQuery(activeQuery);
  }, [activeQuery, submitQuery]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Submit on Enter (no shift); allow Shift+Enter for newline
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void submitQuery(query);
      }
    },
    [query, submitQuery],
  );

  const isLoading = status === "loading";

  return (
    <div className="brain-page" data-testid="brain-page">
      {/* Page header */}
      <header className="brain-header">
        <div className="brain-header__title-block">
          <h1 className="brain-header__title">Brain</h1>
          <span className="brain-header__subtitle">GraphRAG memory retrieval</span>
        </div>
        <div className="brain-header__badge" aria-label="Powered by GraphRAG">
          <span className="brain-header__badge-dot" aria-hidden="true" />
          GraphRAG
        </div>
      </header>

      {/* Two-column layout: main Q&A + history sidebar */}
      <div className="brain-layout">
        {/* Main column */}
        <main className="brain-main" aria-label="Brain Q&A">
          {/* Question form */}
          <form
            className="brain-form"
            onSubmit={handleSubmit}
            aria-label="Ask the Brain"
            role="search"
          >
            <label htmlFor="brain-input" className="brain-form__label">
              Ask a question
            </label>
            <div className="brain-form__input-wrap">
              <textarea
                id="brain-input"
                ref={inputRef}
                className="brain-form__input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="What am I forgetting?"
                rows={2}
                disabled={isLoading}
                aria-label="Question input"
                aria-describedby="brain-input-hint"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="submit"
                className="brain-form__submit"
                disabled={isLoading || !query.trim()}
                aria-label="Submit question"
              >
                {isLoading ? (
                  <span className="brain-form__submit-spinner" aria-hidden="true" />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 8h10M9 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            <p id="brain-input-hint" className="brain-form__hint">
              Press <kbd>Enter</kbd> to ask &middot; <kbd>Shift+Enter</kbd> for a new line
            </p>
          </form>

          {/* Answer / state area */}
          <div className="brain-content" aria-live="polite" aria-atomic="false">
            {status === "idle" && (
              <EmptyState onSelect={handleSuggestionSelect} />
            )}

            {status === "loading" && (
              <ThinkingState query={activeQuery} />
            )}

            {status === "success" && (
              <AnswerPanel
                query={activeQuery}
                answer={answer}
                results={results}
                citations={citations}
              />
            )}

            {status === "error" && (
              <ErrorState message={errorMessage} onRetry={handleRetry} />
            )}

            {status === "unavailable" && (
              <UnavailableState onSelect={handleSuggestionSelect} />
            )}
          </div>
        </main>

        {/* History sidebar */}
        {history.length > 0 && (
          <aside className="brain-history" aria-label="Question history">
            <h2 className="brain-history__title">History</h2>
            <div className="brain-history__list" role="list">
              {history.map((entry) => (
                <HistoryItem
                  key={entry.id}
                  entry={entry}
                  onSelect={handleHistorySelect}
                />
              ))}
            </div>
          </aside>
        )}
      </div>

      {/* Inline styles scoped to brain page */}
      <style>{`
        /* ── Brain page layout ───────────────────────────────── */

        .brain-page {
          display: flex;
          flex-direction: column;
          gap: 0;
          max-width: 1100px;
          min-height: calc(100dvh - var(--header-h) - 64px);
        }

        .brain-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          flex: 1;
        }

        @media (min-width: 900px) {
          .brain-layout {
            grid-template-columns: 1fr 240px;
          }
        }

        /* ── Header ──────────────────────────────────────────── */

        .brain-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--c-border);
          margin-bottom: 28px;
          gap: 16px;
        }

        .brain-header__title-block {
          display: flex;
          align-items: baseline;
          gap: 14px;
        }

        .brain-header__title {
          font-family: var(--font-display);
          font-weight: 800;
          font-size: clamp(26px, 3.5vw, 38px);
          letter-spacing: -0.03em;
          color: var(--c-text-primary);
          line-height: 1;
        }

        .brain-header__subtitle {
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--c-text-muted);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .brain-header__badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--c-accent);
          background: var(--c-accent-dim);
          border: 1px solid rgba(245, 166, 35, 0.18);
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          white-space: nowrap;
        }

        .brain-header__badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--c-accent);
          animation: brain-pulse 2s ease-in-out infinite;
        }

        @keyframes brain-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        /* ── Form ────────────────────────────────────────────── */

        .brain-form {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 28px;
        }

        .brain-form__label {
          font-size: 11px;
          font-weight: 500;
          color: var(--c-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-family: var(--font-mono);
        }

        .brain-form__input-wrap {
          position: relative;
          display: flex;
          align-items: flex-end;
          gap: 0;
        }

        .brain-form__input {
          flex: 1;
          background: var(--c-surface-1);
          border: 1px solid var(--c-border-strong);
          border-right: none;
          border-radius: var(--radius-md) 0 0 var(--radius-md);
          padding: 14px 16px;
          font-family: var(--font-body);
          font-size: 15px;
          font-weight: 300;
          color: var(--c-text-primary);
          resize: none;
          line-height: 1.5;
          outline: none;
          transition: border-color var(--t-base), background var(--t-base);
          min-height: 54px;
        }

        .brain-form__input::placeholder {
          color: var(--c-text-muted);
          font-style: italic;
        }

        .brain-form__input:focus {
          border-color: rgba(245, 166, 35, 0.4);
          background: var(--c-surface-2);
        }

        .brain-form__input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .brain-form__submit {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          min-height: 54px;
          background: var(--c-accent);
          color: var(--c-text-inverse);
          border: 1px solid var(--c-accent);
          border-radius: 0 var(--radius-md) var(--radius-md) 0;
          cursor: pointer;
          transition: background var(--t-fast), opacity var(--t-fast);
          flex-shrink: 0;
        }

        .brain-form__submit:hover:not(:disabled) {
          background: var(--c-accent-hover);
        }

        .brain-form__submit:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .brain-form__submit-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(13, 15, 18, 0.3);
          border-top-color: var(--c-text-inverse);
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .brain-form__hint {
          font-size: 11px;
          color: var(--c-text-muted);
          font-family: var(--font-mono);
        }

        .brain-form__hint kbd {
          display: inline-block;
          font-family: var(--font-mono);
          font-size: 10px;
          background: var(--c-surface-2);
          border: 1px solid var(--c-border-strong);
          border-radius: 3px;
          padding: 1px 4px;
          color: var(--c-text-secondary);
        }

        /* ── Content area (state transitions) ───────────────── */

        .brain-content {
          animation: brain-fade-in 0.18s ease both;
        }

        @keyframes brain-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── Suggestions ─────────────────────────────────────── */

        .brain-suggestions {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 12px;
        }

        .brain-suggestions__chip {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--c-surface-1);
          border: 1px solid var(--c-border);
          border-radius: var(--radius-md);
          color: var(--c-text-secondary);
          font-size: 13.5px;
          font-weight: 400;
          text-align: left;
          cursor: pointer;
          transition: background var(--t-fast), border-color var(--t-fast), color var(--t-fast);
          width: 100%;
          font-family: var(--font-body);
        }

        .brain-suggestions__chip:hover {
          background: var(--c-surface-2);
          border-color: var(--c-border-strong);
          color: var(--c-text-primary);
        }

        .brain-suggestions__chip-icon {
          color: var(--c-accent);
          font-size: 14px;
          flex-shrink: 0;
        }

        /* ── Empty state ─────────────────────────────────────── */

        .brain-empty {
          padding: 20px 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .brain-empty__glyph {
          color: var(--c-text-muted);
          margin-bottom: 12px;
        }

        .brain-empty__prompt {
          font-size: 15px;
          color: var(--c-text-secondary);
          font-weight: 300;
          line-height: 1.6;
        }

        .brain-empty__hint {
          font-size: 12px;
          color: var(--c-text-muted);
          font-family: var(--font-mono);
          margin-top: 4px;
          margin-bottom: 2px;
        }

        /* ── Unavailable state ───────────────────────────────── */

        .brain-unavailable {
          padding: 20px 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .brain-unavailable__header {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .brain-unavailable__dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--c-yellow);
          flex-shrink: 0;
        }

        .brain-unavailable__label {
          font-family: var(--font-mono);
          font-size: 12px;
          font-weight: 500;
          color: var(--c-yellow);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .brain-unavailable__desc {
          font-size: 13px;
          color: var(--c-text-muted);
          line-height: 1.65;
          padding: 12px 14px;
          background: var(--c-surface-1);
          border: 1px solid var(--c-border);
          border-left: 3px solid var(--c-yellow);
          border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
        }

        .brain-unavailable__hint {
          font-size: 12px;
          color: var(--c-text-muted);
          font-family: var(--font-mono);
        }

        .brain-code {
          font-family: var(--font-mono);
          font-size: 12px;
          background: var(--c-surface-2);
          padding: 1px 5px;
          border-radius: 3px;
          color: var(--c-accent);
        }

        /* ── Thinking / loading state ────────────────────────── */

        .brain-thinking {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 24px 0;
          color: var(--c-text-muted);
        }

        .brain-thinking__dots {
          display: flex;
          gap: 5px;
          flex-shrink: 0;
        }

        .brain-thinking__dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--c-accent);
          animation: brain-dot-bounce 1.2s ease-in-out infinite;
        }

        .brain-thinking__dot:nth-child(2) {
          animation-delay: 0.18s;
        }

        .brain-thinking__dot:nth-child(3) {
          animation-delay: 0.36s;
        }

        @keyframes brain-dot-bounce {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        .brain-thinking__label {
          font-size: 13px;
          color: var(--c-text-secondary);
          font-style: normal;
        }

        .brain-thinking__label em {
          color: var(--c-text-primary);
          font-style: italic;
        }

        /* ── Answer panel ────────────────────────────────────── */

        .brain-answer {
          display: flex;
          flex-direction: column;
          gap: 24px;
          animation: brain-fade-in 0.22s ease both;
        }

        .brain-answer__question-echo {
          display: flex;
          align-items: baseline;
          gap: 10px;
          padding-bottom: 14px;
          border-bottom: 1px solid var(--c-border);
        }

        .brain-answer__question-label {
          font-family: var(--font-mono);
          font-size: 10px;
          font-weight: 500;
          color: var(--c-accent);
          background: var(--c-accent-dim);
          padding: 2px 6px;
          border-radius: 3px;
          letter-spacing: 0.06em;
          flex-shrink: 0;
        }

        .brain-answer__question-text {
          font-size: 14px;
          font-weight: 500;
          color: var(--c-text-primary);
          line-height: 1.5;
        }

        .brain-answer__body {
          background: var(--c-surface-1);
          border: 1px solid var(--c-border);
          border-radius: var(--radius-md);
          padding: 18px 20px;
        }

        .brain-answer__text {
          font-size: 14.5px;
          color: var(--c-text-primary);
          line-height: 1.72;
          font-weight: 300;
          white-space: pre-wrap;
        }

        .brain-answer__no-text {
          font-size: 13px;
          color: var(--c-text-muted);
          font-style: italic;
        }

        .brain-answer__sources {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .brain-answer__sources-title {
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 500;
          color: var(--c-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .brain-answer__sources-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--c-surface-2);
          font-size: 10px;
          color: var(--c-text-secondary);
          font-family: var(--font-mono);
        }

        /* ── Citations ───────────────────────────────────────── */

        .brain-citations {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .brain-citation {
          background: var(--c-surface-1);
          border: 1px solid var(--c-border);
          border-radius: var(--radius-md);
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          transition: border-color var(--t-fast);
        }

        .brain-citation:hover {
          border-color: var(--c-border-strong);
        }

        .brain-citation__header {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .brain-citation__index {
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 500;
          color: var(--c-accent);
          flex-shrink: 0;
          min-width: 24px;
        }

        .brain-citation__meta {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
          flex-wrap: wrap;
        }

        .brain-citation__entity {
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--c-text-secondary);
          background: var(--c-surface-2);
          padding: 2px 7px;
          border-radius: 3px;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .brain-citation__graph-badge {
          font-family: var(--font-mono);
          font-size: 10px;
          color: var(--c-blue);
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          padding: 1px 5px;
          border-radius: 3px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .brain-citation__score-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
          margin-left: auto;
        }

        .brain-citation__score-bar {
          height: 3px;
          background: var(--c-accent);
          border-radius: 2px;
          min-width: 4px;
          max-width: 60px;
          transition: width 0.4s ease;
        }

        .brain-citation__score-label {
          font-family: var(--font-mono);
          font-size: 10px;
          color: var(--c-text-muted);
          white-space: nowrap;
        }

        .brain-citation__content {
          font-size: 12.5px;
          color: var(--c-text-secondary);
          line-height: 1.65;
          /* Truncate very long memory excerpts */
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* ── Inline citations (from BrainContext.citations) ──── */

        .brain-inline-citations {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .brain-inline-citation {
          display: flex;
          align-items: center;
          gap: 6px;
          background: var(--c-surface-1);
          border: 1px solid var(--c-border);
          border-radius: var(--radius-sm);
          padding: 4px 10px;
        }

        .brain-inline-citation__index {
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--c-accent);
        }

        .brain-inline-citation__entity {
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--c-text-secondary);
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .brain-inline-citation__score {
          font-family: var(--font-mono);
          font-size: 10px;
          color: var(--c-text-muted);
        }

        /* ── Error state ─────────────────────────────────────── */

        .brain-error {
          padding: 16px 18px;
          background: rgba(239, 68, 68, 0.05);
          border: 1px solid rgba(239, 68, 68, 0.15);
          border-radius: var(--radius-md);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .brain-error__header {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .brain-error__icon {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--c-red);
          color: white;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-family: var(--font-mono);
        }

        .brain-error__title {
          font-size: 13px;
          font-weight: 500;
          color: #fca5a5;
        }

        .brain-error__message {
          font-size: 12.5px;
          color: var(--c-text-muted);
          font-family: var(--font-mono);
          line-height: 1.6;
        }

        .brain-error__retry {
          align-self: flex-start;
          font-size: 12px;
          font-weight: 500;
          color: var(--c-red);
          border: 1px solid rgba(239, 68, 68, 0.3);
          padding: 4px 12px;
          border-radius: var(--radius-sm);
          background: none;
          cursor: pointer;
          font-family: var(--font-body);
          transition: background var(--t-fast), color var(--t-fast);
        }

        .brain-error__retry:hover {
          background: rgba(239, 68, 68, 0.08);
          color: #fca5a5;
        }

        /* ── History sidebar ─────────────────────────────────── */

        .brain-history {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .brain-history__title {
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 500;
          color: var(--c-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--c-border);
        }

        .brain-history__list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .brain-history__item {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 8px 10px;
          background: none;
          border: 1px solid transparent;
          border-radius: var(--radius-sm);
          cursor: pointer;
          text-align: left;
          font-family: var(--font-body);
          transition: background var(--t-fast), border-color var(--t-fast);
          width: 100%;
        }

        .brain-history__item:hover {
          background: var(--c-surface-1);
          border-color: var(--c-border);
        }

        .brain-history__item-query {
          font-size: 12.5px;
          color: var(--c-text-secondary);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .brain-history__item-time {
          font-family: var(--font-mono);
          font-size: 10px;
          color: var(--c-text-muted);
        }
      `}</style>
    </div>
  );
}
